import { useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { Fragment, memo } from 'react';
import {
  RegistrationCitizenshipFormVM,
  OpenLegisVM,
  ToDPRVM,
  ChoiceItalianVM,
  foreignCitizenshipVM,
  OpeLegisVM,
  ForeignNaturalizationVM,
  RenunciationCitizenshipVM,
  DoubleCitizenshipVM,
  ItalianCitizenshipDPVM,
  CitizenshipFormVM,
} from 'mutation/view-models/registration-citizenship';
import OpenLegis from './open-legis';
import ToDPR from './to-dpr';
import ChoiceItalian from './choice-italian';
import ForeignCitizenship from './foreign-citizenship';
import OpeLegis from './ope-legis';
import ForeignNaturalization from './foreign-naturalization';
import RenunciationCitizenship from './renunciation-citizenship';
import DoubleCitizenship from './double-citizenship';
import ItalianCitizenshipDP from './italian-citizenship-dp';
import { StateRegistrationCitizenship } from 'mutation/constants/state-registration-citizenship';

interface FormReasonCitizenshipProps {
  isNationalSearch?: string;
  disabled?: boolean;
}

const FormReasonCitizenshipComponent = ({
  isNationalSearch,
  disabled,
}: FormReasonCitizenshipProps): React.ReactElement => {
  const formContext = useFormikContext<RegistrationCitizenshipFormVM>();

  const onChangeOpenLegis = useEventCallback(
    (key: keyof OpenLegisVM, value: OpenLegisVM[keyof OpenLegisVM]) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          openLegis: {
            ...formContext.values?.newCitizenship?.openLegis,
            [key]: value,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeToDPR = useEventCallback((key: keyof ToDPRVM, value: ToDPRVM[keyof ToDPRVM]) => {
    const newValues = {
      ...formContext.values,
      newCitizenship: {
        ...formContext.values?.newCitizenship,
        toDPR: {
          ...formContext.values?.newCitizenship?.toDPR,
          [key]: value,
        },
      },
    };

    formContext.setValues(newValues);
  });

  const onChangeValuesToDPR = useEventCallback((values: ToDPRVM) => {
    const newValues = {
      ...formContext.values,
      newCitizenship: {
        ...formContext.values?.newCitizenship,
        toDPR: {
          ...formContext.values?.newCitizenship?.toDPR,
          ...values,
        },
      },
    };

    formContext.setValues(newValues);
  });

  const onChangeChoiceItalian = useEventCallback(
    (key: keyof ChoiceItalianVM, value: ChoiceItalianVM[keyof ChoiceItalianVM]) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          choiceItalian: {
            ...formContext.values?.newCitizenship?.choiceItalian,
            [key]: value,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeValuesChoiceItalian = useEventCallback((values: ChoiceItalianVM) => {
    const newValues = {
      ...formContext.values,
      newCitizenship: {
        ...formContext.values?.newCitizenship,
        choiceItalian: {
          ...formContext.values?.newCitizenship?.choiceItalian,
          ...values,
        },
      },
    };

    formContext.setValues(newValues);
  });

  const onChangeForeignCitizenship = useEventCallback(
    (key: keyof foreignCitizenshipVM, value: foreignCitizenshipVM[keyof foreignCitizenshipVM]) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          foreignCitizenship: {
            ...formContext.values?.newCitizenship?.foreignCitizenship,
            [key]: value,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeValuesForeignCitizenship = useEventCallback((values: foreignCitizenshipVM) => {
    const newValues = {
      ...formContext.values,
      newCitizenship: {
        ...formContext.values?.newCitizenship,
        foreignCitizenship: {
          ...formContext.values?.newCitizenship?.foreignCitizenship,
          ...values,
        },
      },
    };

    formContext.setValues(newValues);
  });

  const onChangeOpeLegis = useEventCallback(
    (key: keyof OpeLegisVM, value: OpeLegisVM[keyof OpeLegisVM]) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          opeLegis: {
            ...formContext.values?.newCitizenship?.opeLegis,
            [key]: value,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeForeignNaturalization = useEventCallback(
    (
      key: keyof ForeignNaturalizationVM,
      value: ForeignNaturalizationVM[keyof ForeignNaturalizationVM]
    ) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          foreignNaturalization: {
            ...formContext.values?.newCitizenship?.foreignNaturalization,
            [key]: value,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeValuesForeignNaturalization = useEventCallback(
    (values: ForeignNaturalizationVM) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          foreignNaturalization: {
            ...formContext.values?.newCitizenship?.foreignNaturalization,
            ...values,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeRenunciationCitizenship = useEventCallback(
    (
      key: keyof RenunciationCitizenshipVM,
      value: RenunciationCitizenshipVM[keyof RenunciationCitizenshipVM]
    ) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          renunciationCitizenship: {
            ...formContext.values?.newCitizenship?.renunciationCitizenship,
            [key]: value,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeValuesRenunciationCitizenship = useEventCallback(
    (values: RenunciationCitizenshipVM) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          renunciationCitizenship: {
            ...formContext.values?.newCitizenship?.renunciationCitizenship,
            ...values,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeDoubleCitizenship = useEventCallback(
    (key: keyof DoubleCitizenshipVM, value: DoubleCitizenshipVM[keyof DoubleCitizenshipVM]) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          doubleCitizenship: {
            ...formContext.values?.newCitizenship?.doubleCitizenship,
            [key]: value,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeItalianCitizenshipDP = useEventCallback(
    (
      key: keyof ItalianCitizenshipDPVM,
      value: ItalianCitizenshipDPVM[keyof ItalianCitizenshipDPVM]
    ) => {
      const newValues = {
        ...formContext.values,
        newCitizenship: {
          ...formContext.values?.newCitizenship,
          italianCitizenshipDP: {
            ...formContext.values?.newCitizenship?.italianCitizenshipDP,
            [key]: value,
          },
        },
      };

      formContext.setValues(newValues);
    }
  );

  const onChangeValuesItalianCitizenshipDP = useEventCallback((values: ItalianCitizenshipDPVM) => {
    const newValues = {
      ...formContext.values,
      newCitizenship: {
        ...formContext.values?.newCitizenship,
        italianCitizenshipDP: {
          ...formContext.values?.newCitizenship?.italianCitizenshipDP,
          ...values,
        },
      },
    };

    formContext.setValues(newValues);
  });

  return (
    <Fragment>
      {formContext.values?.newCitizenship?.reasonCitizenship ===
      StateRegistrationCitizenship.OpenLegis ? (
        <OpenLegis
          onChange={onChangeOpenLegis}
          values={formContext.values?.newCitizenship?.openLegis}
          idReason={formContext.values?.newCitizenship?.reasonCitizenship}
          disabled={disabled}
        />
      ) : undefined}
      {formContext.values?.newCitizenship?.reasonCitizenship ===
      StateRegistrationCitizenship.ToDPR ? (
        <ToDPR
          onChange={onChangeToDPR}
          onChangeValues={onChangeValuesToDPR}
          values={formContext.values?.newCitizenship?.toDPR}
          errors={
            (formContext.errors.newCitizenship as FormikErrors<CitizenshipFormVM>)
              ?.toDPR as FormikErrors<ToDPRVM>
          }
          touched={
            (formContext.touched.newCitizenship as unknown as FormikTouched<CitizenshipFormVM>)
              ?.toDPR as unknown as FormikTouched<ToDPRVM>
          }
          idReason={formContext.values?.newCitizenship?.reasonCitizenship}
          disabled={disabled}
        />
      ) : undefined}
      {formContext.values?.newCitizenship?.reasonCitizenship ===
      StateRegistrationCitizenship.ChoiceItalian ? (
        <ChoiceItalian
          onChange={onChangeChoiceItalian}
          onChangeValues={onChangeValuesChoiceItalian}
          values={formContext.values?.newCitizenship?.choiceItalian}
          errors={
            (formContext.errors.newCitizenship as FormikErrors<CitizenshipFormVM>)
              ?.choiceItalian as FormikErrors<ChoiceItalianVM>
          }
          touched={
            (formContext.touched.newCitizenship as unknown as FormikTouched<CitizenshipFormVM>)
              ?.choiceItalian as unknown as FormikTouched<ChoiceItalianVM>
          }
          idReason={formContext.values?.newCitizenship?.reasonCitizenship}
          disabled={disabled}
        />
      ) : undefined}
      {formContext.values?.newCitizenship?.reasonCitizenship ===
      StateRegistrationCitizenship.ForeignCitizenship ? (
        <ForeignCitizenship
          onChange={onChangeForeignCitizenship}
          onChangeValues={onChangeValuesForeignCitizenship}
          values={formContext.values?.newCitizenship?.foreignCitizenship}
          errors={
            (formContext.errors.newCitizenship as FormikErrors<CitizenshipFormVM>)
              ?.foreignCitizenship as FormikErrors<foreignCitizenshipVM>
          }
          touched={
            (formContext.touched.newCitizenship as unknown as FormikTouched<CitizenshipFormVM>)
              ?.foreignCitizenship as unknown as FormikTouched<foreignCitizenshipVM>
          }
          idReason={formContext.values?.newCitizenship?.reasonCitizenship}
          disabled={disabled}
        />
      ) : undefined}
      {formContext.values?.newCitizenship?.reasonCitizenship ===
      StateRegistrationCitizenship.OpeLegis ? (
        <OpeLegis
          onChange={onChangeOpeLegis}
          values={formContext.values?.newCitizenship?.opeLegis}
          idReason={formContext.values?.newCitizenship?.reasonCitizenship}
          isNationalSearch={isNationalSearch}
          disabled={disabled}
        />
      ) : undefined}
      {formContext.values?.newCitizenship?.reasonCitizenship ===
      StateRegistrationCitizenship.ForeignNaturalization ? (
        <ForeignNaturalization
          onChange={onChangeForeignNaturalization}
          onChangeValues={onChangeValuesForeignNaturalization}
          values={formContext.values?.newCitizenship?.foreignNaturalization}
          errors={
            (formContext.errors.newCitizenship as FormikErrors<CitizenshipFormVM>)
              ?.foreignNaturalization as FormikErrors<ForeignNaturalizationVM>
          }
          touched={
            (formContext.touched.newCitizenship as unknown as FormikTouched<CitizenshipFormVM>)
              ?.foreignNaturalization as unknown as FormikTouched<ForeignNaturalizationVM>
          }
          idReason={formContext.values?.newCitizenship?.reasonCitizenship}
          isNationalSearch={isNationalSearch}
          disabled={disabled}
        />
      ) : undefined}
      {formContext.values?.newCitizenship?.reasonCitizenship ===
      StateRegistrationCitizenship.RenunciationCitizenship ? (
        <RenunciationCitizenship
          onChange={onChangeRenunciationCitizenship}
          onChangeValues={onChangeValuesRenunciationCitizenship}
          values={formContext.values?.newCitizenship?.renunciationCitizenship}
          errors={
            (formContext.errors.newCitizenship as FormikErrors<CitizenshipFormVM>)
              ?.renunciationCitizenship as FormikErrors<RenunciationCitizenshipVM>
          }
          touched={
            (formContext.touched.newCitizenship as unknown as FormikTouched<CitizenshipFormVM>)
              ?.renunciationCitizenship as unknown as FormikTouched<RenunciationCitizenshipVM>
          }
          idReason={formContext.values?.newCitizenship?.reasonCitizenship}
          isNationalSearch={isNationalSearch}
          disabled={disabled}
        />
      ) : undefined}
      {formContext.values?.newCitizenship?.reasonCitizenship ===
      StateRegistrationCitizenship.DoubleCitizenship ? (
        <DoubleCitizenship
          onChange={onChangeDoubleCitizenship}
          values={formContext.values?.newCitizenship?.doubleCitizenship}
          idReason={formContext.values?.newCitizenship?.reasonCitizenship}
          disabled={disabled}
        />
      ) : undefined}
      {formContext.values?.newCitizenship?.reasonCitizenship ===
      StateRegistrationCitizenship.ItalianCitizenshipDP ? (
        <ItalianCitizenshipDP
          onChange={onChangeItalianCitizenshipDP}
          onChangeValues={onChangeValuesItalianCitizenshipDP}
          values={formContext.values?.newCitizenship?.italianCitizenshipDP}
          errors={
            (formContext.errors.newCitizenship as FormikErrors<CitizenshipFormVM>)
              ?.italianCitizenshipDP as FormikErrors<ItalianCitizenshipDPVM>
          }
          touched={
            (formContext.touched.newCitizenship as unknown as FormikTouched<CitizenshipFormVM>)
              ?.italianCitizenshipDP as unknown as FormikTouched<ItalianCitizenshipDPVM>
          }
          idReason={formContext.values?.newCitizenship?.reasonCitizenship}
          disabled={disabled}
        />
      ) : undefined}
    </Fragment>
  );
};

FormReasonCitizenshipComponent.displayName = 'Form Accordion Registration Citizenship component';

const FormReasonCitizenship = memo(FormReasonCitizenshipComponent);

export default FormReasonCitizenship;
